import {
  createContext,
  memo,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { TrashSimple } from "@phosphor-icons/react"
import type {
  GridApi,
  GridReadyEvent,
  IDetailCellRendererParams,
  IRowNode,
  SelectionChangedEvent,
} from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { sum, sumBy } from "lodash"

import { Button, Checkbox, DateRangePicker, Input } from "~/components/design-system/components"
import ProductTableV2 from "~/components/planning/ProductTable/ProductTableV2"
import { AggregationMethod } from "~/components/reusable/AggregationSelector"
import { DialogRoot, DialogContent, DialogTitle } from "~/components/reusable/Dialog"
import { Label } from "~/components/reusable/Label"
import { ProductTableType } from "~/constants/productTable"
import { isUserSelectionSource } from "~/utils/ag-utils"
import { SelectedVariantsProvider } from "~/context/SelectedVariantsProvider"
import { useAllocationInventoryMap } from "~/hooks/useAllocationInventoryMap"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useVariants } from "~/hooks/useVariants"
import {
  differenceInCalendarDaysUTC,
  startOfDayUTC,
  startOfTodayUTC,
  toUTCDateString,
} from "~/utils/datetime"
import { cn } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"
import { distinctValues, groupBy } from "~/utils/tsUtils"
import { ProductTableColumn } from "~/utils/productTableUtils"
import type { AllocationInventoryMap } from "~/utils/replenishmentLines"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { showErrorToast } from "~/utils/toaster"
import type { RangePackage } from "./types"
import { useAllocationMutations, useAllocationPackages } from "./useAllocationData"

/**
 * Two-step "New product package" wizard from the allocation-planning design:
 * step 1 collects the basics (name, Seasonal/NOOS, sale range), step 2 is the
 * range picker: a searchable, grouped variant list on the left and the
 * selected range on the right. The same picker serves "Edit range" on an
 * existing package (single step, saves variantIds only).
 */

export type PackageWizardMode =
  | { kind: "create"; folderId: string | null }
  | { kind: "editRange"; pkg: RangePackage }

type PackageWizardProps = {
  mode: PackageWizardMode
  onClose: () => void
  onCreated?: (packageId: string) => void
}

type PackageKind = RangePackage["type"]

function getKindOptions(): { value: PackageKind; label: string; description: string }[] {
  return [
    {
      value: "seasonal",
      label: "Seasonal",
      description: `Carries a ${formatName("sale")} range: a ${formatName("sale")} start and a replenishment cut-off.`,
    },
    {
      value: "noos",
      label: "NOOS",
      description: "Never out of stock: always eligible, no end date. Replenishes until archived.",
    },
    {
      value: "clearance",
      label: "Clearance",
      description:
        "Products being sold out: a start date and a replenishment cut-off, like Seasonal.",
    },
  ]
}

function ColourSwatch({ color }: { color: string | null }) {
  return (
    <span
      title={color ?? undefined}
      className="size-2.5 shrink-0 rounded-full border border-base bg-subtle"
      style={color ? { backgroundColor: color.toLowerCase().replace(/\s/g, "") } : undefined}
    />
  )
}

function sortedSizes(variant: IVariantSummary) {
  return distinctValues(variant.sizes).sort(sortSizesComparator)
}

function formatSizeSpan(sizeNames: string[]) {
  if (sizeNames.length === 0) return "no size data"
  if (sizeNames.length === 1) return sizeNames[0]
  return `${sizeNames[0]}–${sizeNames[sizeNames.length - 1]}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1: basics
// ─────────────────────────────────────────────────────────────────────────────

function RangePreview({ start, cutoff }: { start: Date; cutoff: Date }) {
  const today = startOfTodayUTC()
  const sellDays = differenceInCalendarDaysUTC(cutoff, start)
  const todayOffset = differenceInCalendarDaysUTC(today, start)
  const todayIn = todayOffset >= 0 && todayOffset <= sellDays
  return (
    <div className="rounded-lg border border-base p-3.5">
      <div className="mb-2.5 flex items-baseline gap-2.5">
        <span className="label-sm text-fg-muted">Range preview</span>
        <span className="paragraph-xs text-fg-muted">
          {sellDays} day{sellDays === 1 ? "" : "s"} of selling
        </span>
      </div>
      <div className="relative h-7 overflow-hidden rounded-md border border-base bg-subtle">
        <div className="absolute inset-y-0 left-0 w-full bg-highlight" />
        {todayIn && (
          <div
            className="absolute inset-y-0 w-px bg-fg-base"
            style={{ left: `${(todayOffset / Math.max(1, sellDays)) * 100}%` }}
          />
        )}
      </div>
      <div className="paragraph-xs mt-1.5 flex justify-between gap-2 text-fg-muted tabular-nums">
        <span>{`${start.toISOString().slice(0, 10)} · ${formatName("sale")} start`}</span>
        {todayIn && <span>today</span>}
        <span>{cutoff.toISOString().slice(0, 10)} · replenishment cut-off</span>
      </div>
    </div>
  )
}

function BasicsStep({
  name,
  onNameChange,
  nameError,
  kind,
  onKindChange,
  startDate,
  onStartDateChange,
  cutoffDate,
  onCutoffDateChange,
  dateError,
  dateWarning,
}: {
  name: string
  onNameChange: (v: string) => void
  nameError: string | null
  kind: PackageKind
  onKindChange: (k: PackageKind) => void
  startDate: string
  onStartDateChange: (v: string) => void
  cutoffDate: string
  onCutoffDateChange: (v: string) => void
  dateError: string | null
  dateWarning: string | null
}) {
  const start = startDate ? startOfDayUTC(startDate) : null
  const cutoff = cutoffDate ? startOfDayUTC(cutoffDate) : null
  const showPreview = kind !== "noos" && start && cutoff && !dateError

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pkg-wizard-name">Package name</Label>
          <Input
            id="pkg-wizard-name"
            containerClassName="w-full"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. AW26 core knitwear"
          />
          {nameError && <span className="paragraph-xs text-badge-red-fg-text">{nameError}</span>}
          <span className="paragraph-xs text-fg-muted">
            {`${formatName("planner", true, true)} pick packages by name in filters and rules, so name it after what it contains, not the season alone.`}
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <Label>Type</Label>
          {getKindOptions().map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onKindChange(option.value)}
              className={cn(
                "flex w-full items-start gap-2.5 rounded-lg border p-3 text-left",
                kind === option.value
                  ? "border-primary-500 bg-highlight"
                  : "border-base hover:bg-subtle",
              )}
            >
              <span
                className={cn(
                  "mt-0.5 size-3 shrink-0 rounded-full border-2",
                  kind === option.value
                    ? "border-primary-500 bg-primary-500 shadow-[inset_0_0_0_2px_var(--color-bg-base)]"
                    : "border-border-base",
                )}
              />
              <span className="min-w-0">
                <span className="paragraph-sm block font-semibold text-fg-base">
                  {option.label}
                </span>
                <span className="paragraph-xs block text-fg-subtle">{option.description}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-3.5">
        {kind === "noos" ? (
          <div className="paragraph-xs rounded-lg border border-base bg-subtle p-3.5 text-fg-subtle">
            {`NOOS packages carry no ${formatName("sale")} range. They stay open and keep replenishing until the package is archived, so there are no dates to fill in.`}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Label>{`${formatName("sale", false, true)} range`}</Label>
            <DateRangePicker
              startDate={start ?? undefined}
              endDate={cutoff ?? undefined}
              align="start"
              onApply={({ from, to }) => {
                onStartDateChange(from ? toUTCDateString(from) : "")
                onCutoffDateChange(to ? toUTCDateString(to) : "")
              }}
              className="w-full"
            />
            <span className="paragraph-xs text-fg-muted">
              {`${formatName("sale", false, true)} start is the first day products may ship to stores. The replenishment cut-off is the last day top-ups are suggested.`}
            </span>
          </div>
        )}
        {dateError && (
          <div className="paragraph-xs rounded-lg border border-badge-red-border bg-badge-red-bg p-2.5 text-badge-red-fg-text">
            {dateError}
          </div>
        )}
        {dateWarning && !dateError && (
          <div className="paragraph-xs rounded-lg border border-badge-orange-border bg-badge-orange-bg p-2.5 text-badge-orange-fg-text">
            {dateWarning}
          </div>
        )}
        {showPreview && <RangePreview start={start} cutoff={cutoff} />}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2: range picker + selection panel
// ─────────────────────────────────────────────────────────────────────────────

const MemoizedProductTable = memo(ProductTableV2)

const PICKER_TABLE_ID = "allocation-package-range-picker"

const PICKER_COLUMNS = [
  ProductTableColumn.Variant,
  ProductTableColumn.Category,
  ProductTableColumn.Collection,
  ProductTableColumn.LifecycleStatus,
  ProductTableColumn.StockLevel,
]

function hasSelectedSku(variant: IVariantSummary, selected: Set<string>) {
  return variant.skuIds.some((skuId) => selected.has(skuId))
}

function warehouseStockFor(skuIds: string[], inventory: AllocationInventoryMap) {
  return sumBy(skuIds, (skuId) => sum(Object.values(inventory[skuId]?.byWarehouse ?? {})))
}

function readGridSelection(api: GridApi<IVariantSummary>) {
  const visible: IVariantSummary[] = []
  const picked = new Set<string>()
  api.forEachNode((node) => {
    if (!node.data?.variantId || node.group) return
    visible.push(node.data)
    if (node.isSelected()) picked.add(node.data.variantId)
  })
  return { visible, picked }
}

function revealVariant(api: GridApi<IVariantSummary>, variantId: string) {
  let target: IRowNode<IVariantSummary> | undefined
  api.forEachNodeAfterFilterAndSort((node) => {
    if (!target && !node.group && node.data?.variantId === variantId) target = node
  })
  if (!target) return
  if (target.rowIndex != null) {
    api.paginationGoToPage(Math.floor(target.rowIndex / api.paginationGetPageSize()))
  }
  api.ensureNodeVisible(target, "middle")
  api.flashCells({ rowNodes: [target] })
}

function syncGridSelection(api: GridApi<IVariantSummary>, selected: Set<string>) {
  const toSelect: IRowNode<IVariantSummary>[] = []
  const toDeselect: IRowNode<IVariantSummary>[] = []
  api.forEachNode((node) => {
    if (!node.data?.variantId || node.group) return
    const want = hasSelectedSku(node.data, selected)
    if (want && !node.isSelected()) toSelect.push(node)
    if (!want && node.isSelected()) toDeselect.push(node)
  })
  if (toSelect.length > 0) api.setNodesSelected({ nodes: toSelect, newValue: true, source: "api" })
  if (toDeselect.length > 0)
    api.setNodesSelected({ nodes: toDeselect, newValue: false, source: "api" })
}

const SkuSelectionContext = createContext<{
  selected: Set<string>
  onSetMany: (skuIds: string[], value: boolean) => void
}>({ selected: new Set(), onSetMany: () => {} })

function SkuChecklist({ data }: IDetailCellRendererParams<IVariantSummary>) {
  const { selected, onSetMany } = useContext(SkuSelectionContext)
  const skus = [...(data?.skuData ?? [])].sort((a, b) => sortSizesComparator(a.size, b.size))
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 bg-subtle px-12 py-2.5">
      {skus.map((sku) => (
        <label
          key={sku.skuId}
          className="paragraph-xs flex cursor-pointer items-center gap-2 text-fg-base"
        >
          <Checkbox
            checked={selected.has(sku.skuId)}
            onCheckedChange={(checked) => onSetMany([sku.skuId], checked === true)}
          />
          {sku.size || sku.skuId}
        </label>
      ))}
    </div>
  )
}

function SelectedVariantRow({
  variant,
  selected,
  inventory,
  onReveal,
  onRemove,
}: {
  variant: IVariantSummary
  selected: Set<string>
  inventory: AllocationInventoryMap
  onReveal: () => void
  onRemove: () => void
}) {
  const pickedSkuIds = variant.skuIds.filter((skuId) => selected.has(skuId))
  const stock = warehouseStockFor(pickedSkuIds, inventory)
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onReveal}
      onKeyDown={(e) => {
        if (e.key === "Enter") onReveal()
      }}
      className="flex min-w-0 cursor-pointer items-center gap-2 border-b border-base px-3.5 py-1.5 hover:bg-subtle"
    >
      <ColourSwatch color={variant.color} />
      <div className="min-w-0 flex-1">
        <div className="paragraph-sm truncate font-medium text-fg-base">
          {variant.variantDisplayName}
        </div>
        <div className="paragraph-xs truncate text-fg-muted">
          {formatSizeSpan(sortedSizes(variant))} · {pickedSkuIds.length}/{variant.skuIds.length}{" "}
          SKUs
        </div>
      </div>
      <span
        title="WH total sellable stock: gross sellable on hand at the warehouse before reservations"
        className={cn(
          "paragraph-xs tabular-nums",
          stock === 0 ? "text-badge-orange-fg-text" : "text-fg-subtle",
        )}
      >
        {stock.toLocaleString()}
      </span>
      <Button
        variant="transparent"
        size="xs"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        aria-label={`Remove ${variant.variantDisplayName}`}
      >
        <TrashSimple size={12} />
      </Button>
    </div>
  )
}

type PanelRow =
  | { kind: "group"; category: string; items: IVariantSummary[] }
  | { kind: "variant"; variant: IVariantSummary }

function SelectedRangeList({
  rows,
  selected,
  onSetMany,
  onReveal,
}: {
  rows: PanelRow[]
  selected: Set<string>
  onSetMany: (skuIds: string[], value: boolean) => void
  onReveal: (variantId: string) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current?.parentElement ?? null,
    estimateSize: () => 45,
    overscan: 10,
  })
  const virtualRows = virtualizer.getVirtualItems()
  const onScreenSkuIds = virtualRows.flatMap((virtualRow) => {
    const row = rows[virtualRow.index]
    return row?.kind === "variant" ? row.variant.skuIds.filter((skuId) => selected.has(skuId)) : []
  })
  const { data: inventory = {} } = useAllocationInventoryMap(onScreenSkuIds)

  return (
    <div ref={scrollRef} className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index]
        if (!row) return null
        return (
          <div
            key={row.kind === "group" ? `group:${row.category}` : row.variant.variantId}
            ref={virtualizer.measureElement}
            data-index={virtualRow.index}
            className="absolute inset-x-0 top-0"
            style={{ transform: `translateY(${virtualRow.start}px)` }}
          >
            {row.kind === "group" ? (
              <div className="flex items-center gap-2 border-b border-base bg-subtle px-3.5 py-1.5">
                <span className="paragraph-xs min-w-0 flex-1 truncate font-semibold text-fg-base">
                  {row.category}
                </span>
                <span className="paragraph-xs text-fg-muted tabular-nums">{row.items.length}</span>
                <Button
                  variant="transparent"
                  size="xs"
                  onClick={() =>
                    onSetMany(
                      row.items.flatMap((v) => v.skuIds),
                      false,
                    )
                  }
                >
                  remove
                </Button>
              </div>
            ) : (
              <SelectedVariantRow
                variant={row.variant}
                selected={selected}
                inventory={inventory}
                onReveal={() => onReveal(row.variant.variantId)}
                onRemove={() => onSetMany(row.variant.skuIds, false)}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

function RangePickerStep({
  selected,
  onReplaceVisible,
  onSetMany,
  onClearSelection,
}: {
  selected: Set<string>
  onReplaceVisible: (visible: IVariantSummary[], picked: Set<string>) => void
  onSetMany: (skuIds: string[], value: boolean) => void
  onClearSelection: () => void
}) {
  const variants = useVariants()
  const panelSelected = useDeferredValue(selected)
  const selectedVariants = useMemo(
    () => variants.filter((v) => hasSelectedSku(v, panelSelected)),
    [panelSelected, variants],
  )
  const selectedGroups = useMemo(
    () =>
      Object.entries(groupBy(selectedVariants, (v) => v.category ?? "Other")).sort((a, b) =>
        a[0].localeCompare(b[0]),
      ),
    [selectedVariants],
  )
  const skuSelection = useMemo(() => ({ selected, onSetMany }), [selected, onSetMany])
  const panelRows = useMemo(
    () =>
      selectedGroups.flatMap(([category, items]): PanelRow[] => [
        { kind: "group", category, items },
        ...items.map((variant): PanelRow => ({ kind: "variant", variant })),
      ]),
    [selectedGroups],
  )

  const gridRef = useRef<AgGridReact<IVariantSummary> | null>(null)
  const gridApiRef = useRef<GridApi<IVariantSummary> | null>(null)
  const revealInGrid = useCallback((variantId: string) => {
    if (gridApiRef.current) revealVariant(gridApiRef.current, variantId)
  }, [])
  const selectedRef = useRef(selected)
  const onReplaceVisibleRef = useRef(onReplaceVisible)
  useEffect(() => {
    selectedRef.current = selected
    onReplaceVisibleRef.current = onReplaceVisible
    if (gridApiRef.current) syncGridSelection(gridApiRef.current, selected)
  }, [selected, onReplaceVisible])

  const handleGridReady = useCallback((event: GridReadyEvent<IVariantSummary>) => {
    const api = event.api
    gridApiRef.current = api
    const sync = () => syncGridSelection(api, selectedRef.current)
    api.addEventListener("rowDataUpdated", sync)
    api.addEventListener("selectionChanged", (e: SelectionChangedEvent<IVariantSummary>) => {
      if (!isUserSelectionSource(e.source)) return
      const { visible, picked } = readGridSelection(api)
      onReplaceVisibleRef.current(visible, picked)
    })
    sync()
  }, [])

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col p-4">
        <SkuSelectionContext.Provider value={skuSelection}>
          <SelectedVariantsProvider gridRef={gridRef} persistSelection={false}>
            <MemoizedProductTable
              id={PICKER_TABLE_ID}
              gridRef={gridRef}
              variants={variants}
              type={ProductTableType.None}
              minHeight={0}
              initialAggregation={AggregationMethod.Variant}
              defaultColumnIdsToDisplay={PICKER_COLUMNS}
              enableFilter
              enableSearch
              masterDetail
              detailCellRenderer={SkuChecklist}
              detailRowAutoHeight
              onGridReady={handleGridReady}
            />
          </SelectedVariantsProvider>
        </SkuSelectionContext.Provider>
      </div>

      <div className="flex w-75 shrink-0 flex-col border-l border-base">
        <div className="border-b border-base bg-highlight px-3.5 py-2.5">
          <div className="flex items-center gap-2">
            <span className="paragraph-sm min-w-0 flex-1 font-semibold text-fg-base">
              Selected range
            </span>
            {selected.size > 0 && (
              <Button variant="transparent" size="xs" onClick={onClearSelection}>
                Clear
              </Button>
            )}
          </div>
          <div className="paragraph-xs mt-0.5 text-fg-subtle tabular-nums">
            {selectedVariants.length} variant{selectedVariants.length === 1 ? "" : "s"} ·{" "}
            {selected.size} SKU{selected.size === 1 ? "" : "s"}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SelectedRangeList
            rows={panelRows}
            selected={panelSelected}
            onSetMany={onSetMany}
            onReveal={revealInGrid}
          />
          {selected.size === 0 && (
            <p className="paragraph-xs p-6 text-center text-fg-muted">
              Nothing selected yet. Tick variants on the left, or expand one to pick its SKUs.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Wizard shell
// ─────────────────────────────────────────────────────────────────────────────

export default function PackageWizard({ mode, onClose, onCreated }: PackageWizardProps) {
  const isEditRange = mode.kind === "editRange"
  const editPkg = isEditRange ? mode.pkg : null

  const { packages } = useAllocationPackages()
  const { savePackage } = useAllocationMutations()

  const [step, setStep] = useState<1 | 2>(isEditRange ? 2 : 1)
  const [name, setName] = useState("")
  const [kind, setKind] = useState<PackageKind>("seasonal")
  const [startDate, setStartDate] = useState("")
  const [cutoffDate, setCutoffDate] = useState("")
  const [selected, setSelected] = useState<Set<string>>(() => new Set(editPkg?.skuIds ?? []))

  const nameError = useMemo(() => {
    if (isEditRange) return null
    if (!name.trim()) return null // shown as disabled Continue, not an error while typing
    const normalized = name.trim().toLowerCase()
    return packages.some((p) => p.name.trim().toLowerCase() === normalized)
      ? "A package with this name already exists."
      : null
  }, [isEditRange, name, packages])

  const dateError = useMemo(() => {
    if (isEditRange || kind !== "seasonal" || !startDate || !cutoffDate) return null
    return startOfDayUTC(cutoffDate) <= startOfDayUTC(startDate)
      ? `The replenishment cut-off must be after the ${formatName("sale")} start.`
      : null
  }, [isEditRange, kind, startDate, cutoffDate])

  const dateWarning = useMemo(() => {
    if (isEditRange || kind !== "seasonal" || !cutoffDate) return null
    return startOfDayUTC(cutoffDate) < startOfTodayUTC()
      ? "The cut-off is in the past, so this package will never suggest top-ups."
      : null
  }, [isEditRange, kind, cutoffDate])

  const basicsComplete =
    name.trim().length > 0 &&
    nameError === null &&
    (kind === "noos" || (startDate !== "" && cutoffDate !== "" && dateError === null))

  const replaceVisible = useCallback(
    (visible: IVariantSummary[], picked: Set<string>) =>
      setSelected((prev) => {
        const next = new Set(prev)
        for (const variant of visible) {
          if (!picked.has(variant.variantId)) {
            for (const skuId of variant.skuIds) next.delete(skuId)
          } else if (!hasSelectedSku(variant, prev)) {
            for (const skuId of variant.skuIds) next.add(skuId)
          }
        }
        return next
      }),
    [],
  )
  const setMany = useCallback(
    (skuIds: string[], value: boolean) =>
      setSelected((prev) => {
        const next = new Set(prev)
        for (const skuId of skuIds) {
          if (value) next.add(skuId)
          else next.delete(skuId)
        }
        return next
      }),
    [],
  )

  const handleSave = () => {
    if (savePackage.isPending) return
    const skuIds = [...selected]
    if (editPkg) {
      savePackage.mutate(
        { id: editPkg.id, name: editPkg.name, type: editPkg.type, skuIds },
        { onSuccess: onClose, onError: showErrorToast },
      )
      return
    }
    const start = kind !== "noos" && startDate ? startOfDayUTC(startDate) : null
    savePackage.mutate(
      {
        name: name.trim(),
        type: kind,
        startDate: start,
        cutoffDate: kind !== "noos" && cutoffDate ? startOfDayUTC(cutoffDate) : null,
        skuIds,
        folderId: mode.kind === "create" ? mode.folderId : null,
      },
      {
        onSuccess: ({ packageId }) => {
          onCreated?.(packageId)
          onClose()
        },
        onError: showErrorToast,
      },
    )
  }

  const steps = [
    { num: 1, label: "Basics", sub: name.trim() || `name, type, ${formatName("sale")} range` },
    {
      num: 2,
      label: "Range",
      sub: `${selected.size} SKU${selected.size === 1 ? "" : "s"} selected`,
    },
  ]

  return (
    <DialogRoot open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          "flex flex-col gap-0 p-0",
          step === 2 ? "h-[82vh] max-w-270" : "h-auto max-w-140",
        )}
      >
        <div className="flex shrink-0 flex-wrap items-center gap-x-3.5 gap-y-2 border-b border-base py-2.5 pr-12 pl-4">
          <DialogTitle className="shrink-0">
            {isEditRange ? `Edit range: ${editPkg?.name}` : "New product package"}
          </DialogTitle>
          {!isEditRange && (
            <div className="flex min-w-0 items-center gap-1.5">
              {steps.map((s) => (
                <button
                  key={s.num}
                  type="button"
                  disabled={s.num === 2 && !basicsComplete}
                  onClick={() => setStep(s.num as 1 | 2)}
                  className={cn(
                    "flex min-w-0 items-center gap-2 rounded-lg border px-2.5 py-1",
                    step === s.num ? "border-primary-500 bg-highlight" : "border-base",
                    s.num === 2 && !basicsComplete ? "cursor-not-allowed opacity-60" : "",
                  )}
                >
                  <span
                    className={cn(
                      "flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                      step === s.num
                        ? "bg-primary-500 text-fg-on-color"
                        : "bg-subtle text-fg-subtle",
                    )}
                  >
                    {s.num}
                  </span>
                  <span className="paragraph-xs font-semibold whitespace-nowrap text-fg-base">
                    {s.label}
                  </span>
                  <span className="paragraph-xs max-w-52 truncate text-fg-muted">{s.sub}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {step === 1 && !isEditRange ? (
          <BasicsStep
            name={name}
            onNameChange={setName}
            nameError={nameError}
            kind={kind}
            onKindChange={setKind}
            startDate={startDate}
            onStartDateChange={setStartDate}
            cutoffDate={cutoffDate}
            onCutoffDateChange={setCutoffDate}
            dateError={dateError}
            dateWarning={dateWarning}
          />
        ) : (
          <RangePickerStep
            selected={selected}
            onReplaceVisible={replaceVisible}
            onSetMany={setMany}
            onClearSelection={() => setSelected(new Set())}
          />
        )}

        <div className="flex shrink-0 items-center gap-2 border-t border-base px-4 py-2.5">
          <span className="paragraph-xs min-w-0 text-fg-muted">
            {isEditRange
              ? "Stores keep their assignment; only the product range changes."
              : "Stores are assigned after the package is created."}
          </span>
          <div className="flex-1" />
          <Button variant="neutral" size="sm" onClick={onClose}>
            Cancel
          </Button>
          {!isEditRange && step === 2 && (
            <Button variant="neutral" size="sm" onClick={() => setStep(1)}>
              Back
            </Button>
          )}
          {!isEditRange && step === 1 && (
            <Button
              variant="primary"
              size="sm"
              disabled={!basicsComplete}
              onClick={() => setStep(2)}
            >
              Continue
            </Button>
          )}
          {(isEditRange || step === 2) && (
            <Button
              variant="primary"
              size="sm"
              requires="edit"
              loading={savePackage.isPending}
              disabled={!isEditRange && !basicsComplete}
              onClick={handleSave}
            >
              {isEditRange ? "Save range" : "Create package"}
            </Button>
          )}
        </div>
      </DialogContent>
    </DialogRoot>
  )
}
